// Tema "console operativa" scuro, condiviso da SOC Operations e Overview: le due pagine che si
// guardano a lungo, spesso su uno schermo grande in sala, dove un fondo chiaro stanca e i colori
// di stato risaltano meno. Il resto della piattaforma (griglie, configurazione, report) resta
// chiaro: sono pagine di lavoro puntuale, non di monitoraggio continuo.
//
// I colori dei grafici NON sono un'inversione automatica di quelli chiari: sono i passi pensati
// per fondo scuro, validati con lo script della metodologia dataviz contro la superficie reale
// delle card (#151f33). Esiti della validazione:
//  - bande di priorità (palette di STATO): separazione ΔE 27.6 a vista normale / 11.3 con
//    daltonismo, e qui passa anche il contrasto 3:1 — sul bianco il giallo non ci arrivava.
//  - serie aperti/chiusi (palette CATEGORICA, passi scuri): tutti i controlli superati. Unica
//    nota: con tritanopia blu e aqua sono vicini (ΔE 4.0), quindi l'identità non è mai affidata al
//    solo colore — legenda sempre presente e ordine delle barre fisso.

// Superfici e inchiostri
export const CONSOLE = {
  page: '#0b1220',        // sfondo pagina
  surface: '#151f33',     // card
  surfaceRaised: '#1c2942', // card in evidenza / righe in hover
  border: '#23314d',
  inkPrimary: '#e8edf7',
  inkSecondary: '#9fb0cc',
  inkMuted: '#6b7f9e',
  accent: '#38bdf8',      // ciano di accento, per numeri e dettagli attivi
} as const;

// Bande di priorità: colore = gravità dello stato, non identità di una serie.
export const BAND_COLORS = { critical: '#d03b3b', medium: '#fab219', low: '#0ca30c' } as const;

// Serie categoriche (identità): passi per fondo scuro.
export const SERIES_OPENED = '#3987e5';
export const SERIES_CLOSED = '#199e70';

// Cromature dei grafici su fondo scuro.
export const GRID_INK = '#23314d';
export const AXIS_INK = '#6b7f9e';

// Severità, nella stessa logica di gravità delle bande: usate per i badge dentro le due pagine
// scure (quelle chiare del resto della piattaforma restano invariate).
export const SEVERITY_DARK: Record<string, string> = {
  low: 'bg-slate-700/60 text-slate-300',
  medium: 'bg-amber-500/15 text-amber-300',
  high: 'bg-orange-500/15 text-orange-300',
  critical: 'bg-red-500/20 text-red-300',
};

// Severità su fondo scuro: RAMPA ORDINALE a tinta unica (chiaro = bassa, scuro = critica), non
// quattro tinte diverse. Motivo misurato, non estetico: quattro livelli caldi (rosso/arancio/
// giallo) non si possono distinguere per sola tinta — ogni combinazione provata ha una coppia
// adiacente sotto la soglia di 15 ΔE a vista normale (6.8 fra arancio e rosso, 13.6 fra giallo e
// arancio). Con la rampa ordinale passano tutti i controlli: luminosità monotona, tinta unica
// (30° di escursione), contrasto sul fondo. La gravità la porta l'intensità, non la tinta.
// I badge di severità del resto della piattaforma restano rosso/ambra: lì il colore è accostato
// sempre alla parola, qui invece sono spicchi di una ciambella senza testo dentro.
export const SEVERITY_RAMP: Record<string, string> = {
  low: '#f8d9b0', medium: '#f0a95f', high: '#e0762b', critical: '#b4501a',
};

// Raggiungibilità asset: blu/rosso e NON verde/rosso. Verde e rosso hanno ΔE 4.1 con deuteranopia,
// cioè sono lo stesso colore per circa un uomo su dodici — su una ciambella a due spicchi senza
// testo interno sarebbe illeggibile. Blu contro rosso: 25.7 con daltonismo, 31.9 a vista normale.
export const REACHABLE_HEX = '#3987e5';
export const UNREACHABLE_HEX = '#d03b3b';

// Serie a tinta unica per i grafici a barre con una sola misura (MITRE, host più colpiti, stati):
// l'identità è data dall'etichetta dell'asse, il colore non deve distinguere nulla.
export const SERIES_SINGLE = '#3987e5';
